import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Schedule,
  Parent,
  TimeBlock,
  CycleLength,
  SavedSchedule,
  HolidayOverride,
  HolidayOverrideDetails,
  CustomHoliday,
  CustodyPlanDetails,
} from '../types';

// Sample data for initial state
const sampleSchedule: Schedule = {
  id: '1',
  name: 'Custody Calendar',
  cycleLength: 14,
  parents: [
    { id: 'p1', name: 'Parent A', color: '#7364AF' },
    { id: 'p2', name: 'Parent B', color: '#327B74' }
  ],
  timeBlocks: [],
  templateId: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

/**
 * Store for schedule data and operations
 */
interface ScheduleState {
  // Current active schedule
  schedule: Schedule | null;

  // Finalization - once a calendar is exported as .ics it is locked for editing.
  // Standard/Premium users can unlock for free; Basic users spend a save credit.
  isFinalized: boolean;
  finalizedAt: string | null;
  setIsFinalized: (finalized: boolean) => void;
  clearFinalization: () => void;

  // For first time users
  isFirstTimeUser: boolean;
  setFirstTimeUserCompleted: () => void;
  
  // Operations on the entire schedule
  setSchedule: (schedule: Schedule) => void;
  resetSchedule: () => void;
  
  // Time block operations
  addTimeBlock: (block: Omit<TimeBlock, 'id'>) => void;
  updateTimeBlock: (blockId: string, updates: Partial<TimeBlock>) => void;
  deleteTimeBlock: (blockId: string) => void;
  clearTimeBlocks: () => void;
  
  // Parent operations
  updateParent: (parentId: string, updates: Partial<Parent>) => void;
  
  // Schedule configuration
  updateCycleLength: (length: CycleLength) => void;
  setTemplateId: (templateId: string | null) => void;
  setEndDate: (endDate: string | undefined) => void;
  updatePlanDetails: (updates: Partial<CustodyPlanDetails>) => void;
  
  // Holiday overrides (no credit impact)
  setHolidayOverride: (date: string, parentId: string, details?: HolidayOverrideDetails) => void;
  updateHolidayOverrideDetails: (date: string, details: HolidayOverrideDetails) => void;
  removeHolidayOverride: (date: string) => void;
  addCustomHoliday: (name: string, date: string) => void;
  removeCustomHoliday: (id: string) => void;
  
  // Analysis
  calculateParentTime: (parentId: string) => { percentage: number; overnights: number };
  
  // Saved schedules
  savedSchedules: SavedSchedule[];
  setSavedSchedules: (schedules: SavedSchedule[]) => void;
  addSavedSchedule: (schedule: SavedSchedule) => void;
  removeSavedSchedule: (scheduleId: string) => void;
}

export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      // Initial state
      schedule: sampleSchedule,
      isFirstTimeUser: true,
      savedSchedules: [],
      isFinalized: false,
      finalizedAt: null,

      // Finalization
      setIsFinalized: (finalized) =>
        set({ isFinalized: finalized, finalizedAt: finalized ? new Date().toISOString() : null }),
      clearFinalization: () => set({ isFinalized: false, finalizedAt: null }),

      // First time user handling
      setFirstTimeUserCompleted: () => set({ isFirstTimeUser: false }),
      
      // Schedule operations
      setSchedule: (schedule) => set({ schedule }),
      
      resetSchedule: () => {
        set({
          schedule: {
            ...sampleSchedule,
            id: `schedule_${Date.now()}`,
            name: 'Custody Calendar',
            timeBlocks: [],
            templateId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          // Starting a new calendar always clears the finalized state
          isFinalized: false,
          finalizedAt: null,
        });
      },
      
      // Time block operations
      updateTimeBlock: (blockId, updates) => {
        set((state) => {
          if (!state.schedule) return state;
          
          const updatedBlocks = state.schedule.timeBlocks.map((block) =>
            block.id === blockId ? { ...block, ...updates } : block
          );
          
          return {
            schedule: {
              ...state.schedule,
              timeBlocks: updatedBlocks,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      updateParent: (parentId, updates) => {
        set((state) => {
          if (!state.schedule) return state;

          const updatedParents = state.schedule.parents.map((parent) =>
            parent.id === parentId ? { ...parent, ...updates } : parent
          );

          return {
            schedule: {
              ...state.schedule,
              parents: updatedParents,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },
      
      calculateParentTime: (parentId) => {
        const state = get();
        if (!state.schedule) return { percentage: 0, overnights: 0 };
        
        // We'll use the schedule's timeBlocks directly for calculations
        // No need to use cycleLength or totalBlocks variables
        const parentBlocks = state.schedule.timeBlocks.filter(
          (block) => block.parentId === parentId
        );
        
        // Calculate hours for more accurate time percentage
        let totalHours = 0;
        let parentHours = 0;
        
        state.schedule.timeBlocks.forEach(block => {
          // Calculate hours for each block
          const startTime = new Date(`2000-01-01T${block.startTime}`);
          const endTime = new Date(`2000-01-01T${block.endTime}`);
          
          // Handle blocks that span multiple days
          const days = block.endDayIndex - block.startDayIndex + 1;
          
          // Calculate total hours in block
          let hours = 0;
          
          try {
            if (days > 1) {
              // For multi-day blocks, count full 24-hour days plus partial start/end
              hours = (days - 1) * 24;
              
              // Add partial hours for first day
              const hoursTillMidnight = 24 - startTime.getHours() - startTime.getMinutes() / 60;
              hours += hoursTillMidnight;
              
              // Add partial hours for last day
              const hoursFromMidnight = endTime.getHours() + endTime.getMinutes() / 60;
              hours += hoursFromMidnight;
            } else {
              // For same-day blocks, calculate time difference
              let timeInMs = endTime.getTime() - startTime.getTime();
              if (timeInMs < 0) timeInMs += 24 * 60 * 60 * 1000; // Handle overnight within same day
              hours = timeInMs / (60 * 60 * 1000);
            }
          } catch (error) {
            console.error("Error calculating hours for block:", error);
            // Fallback to simple 12 hours per block
            hours = 12;
          }
          
          totalHours += hours;
          if (block.parentId === parentId) {
            parentHours += hours;
          }
        });
        
        // Count overnights specifically in the current cycle
        const overnights = parentBlocks.filter((block) => block.isOvernight).length;
        
        // Calculate percentage based on hours
        const percentage = totalHours === 0 ? 0 : (parentHours / totalHours) * 100;
        
        return { percentage, overnights };
      },

      addTimeBlock: (block) => {
        set((state) => {
          if (!state.schedule) return state;

          const newBlock = {
            ...block,
            id: `block${Date.now()}`,
          };

          // Check if the block is being added as part of a template application
          // If it is, preserve the templateId, otherwise set it to null
          // We can tell this by seeing if the block is coming from a template generator
          // which will set isPickup or isDropoff flags
          const isTemplateOperation = block.isPickup !== undefined || block.isDropoff !== undefined;

          return {
            schedule: {
              ...state.schedule,
              timeBlocks: [...state.schedule.timeBlocks, newBlock],
              // Only clear templateId if this is a manual user edit, not a template operation
              templateId: isTemplateOperation ? state.schedule.templateId : null,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      updateCycleLength: (length: CycleLength) => {
        set((state) => {
          if (!state.schedule) return state;

          const updatedBlocks = state.schedule.timeBlocks.map(block => ({
            ...block,
            startDayIndex: Math.min(block.startDayIndex, length - 1),
            endDayIndex: Math.min(block.endDayIndex, length - 1)
          }));

          return {
            schedule: {
              ...state.schedule,
              cycleLength: length,
              timeBlocks: updatedBlocks,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      clearTimeBlocks: () => {
        set((state) => {
          if (!state.schedule) return state;

          return {
            schedule: {
              ...state.schedule,
              timeBlocks: [],
              templateId: null, // Reset template ID when clearing
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      deleteTimeBlock: (blockId: string) => {
        set((state) => {
          if (!state.schedule) return state;

          return {
            schedule: {
              ...state.schedule,
              timeBlocks: state.schedule.timeBlocks.filter((block) => block.id !== blockId),
              templateId: null,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      setTemplateId: (templateId: string | null) => {
        set((state) => {
          if (!state.schedule) return state;

          return {
            schedule: {
              ...state.schedule,
              templateId,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      setEndDate: (endDate: string | undefined) => {
        set((state) => {
          if (!state.schedule) return state;
          return {
            schedule: {
              ...state.schedule,
              endDate,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      updatePlanDetails: (updates: Partial<CustodyPlanDetails>) => {
        set((state) => {
          if (!state.schedule) return state;
          return {
            schedule: {
              ...state.schedule,
              planDetails: {
                ...state.schedule.planDetails,
                ...updates,
              },
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      setHolidayOverride: (date: string, parentId: string, details?: HolidayOverrideDetails) => {
        set((state) => {
          if (!state.schedule) return state;
          const overrides = state.schedule.holidayOverrides ?? [];
          const filtered = overrides.filter((o) => o.date !== date);
          const updated: HolidayOverride[] = [...filtered, { date, parentId, ...(details && Object.keys(details).length > 0 ? { details } : {}) }];
          return {
            schedule: {
              ...state.schedule,
              holidayOverrides: updated,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      updateHolidayOverrideDetails: (date: string, details: HolidayOverrideDetails) => {
        set((state) => {
          if (!state.schedule) return state;
          const overrides = state.schedule.holidayOverrides ?? [];
          const existing = overrides.find((o) => o.date === date);
          if (!existing) return state;
          const updated: HolidayOverride[] = overrides.map((o) =>
            o.date === date ? { ...o, details: { ...o.details, ...details } } : o
          );
          return {
            schedule: {
              ...state.schedule,
              holidayOverrides: updated,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      removeHolidayOverride: (date: string) => {
        set((state) => {
          if (!state.schedule) return state;
          const overrides = (state.schedule.holidayOverrides ?? []).filter((o) => o.date !== date);
          return {
            schedule: {
              ...state.schedule,
              holidayOverrides: overrides,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      addCustomHoliday: (name: string, date: string) => {
        set((state) => {
          if (!state.schedule) return state;
          const custom = state.schedule.customHolidays ?? [];
          const newHoliday: CustomHoliday = {
            id: `custom-${date}-${Date.now()}`,
            name,
            date,
          };
          return {
            schedule: {
              ...state.schedule,
              customHolidays: [...custom, newHoliday],
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },

      removeCustomHoliday: (id: string) => {
        set((state) => {
          if (!state.schedule) return state;
          const custom = (state.schedule.customHolidays ?? []).filter((h) => h.id !== id);
          return {
            schedule: {
              ...state.schedule,
              customHolidays: custom,
              updatedAt: new Date().toISOString(),
            },
          };
        });
      },
      
      // Saved schedules operations
      setSavedSchedules: (schedules) => set({ savedSchedules: schedules }),
      
      addSavedSchedule: (schedule) => {
        set((state) => ({
          savedSchedules: [...state.savedSchedules, schedule]
        }));
      },
      
      removeSavedSchedule: (scheduleId) => {
        set((state) => ({
          savedSchedules: state.savedSchedules.filter(s => s.id !== scheduleId)
        }));
      },
    }),
    {
      name: 'custody-planner-schedule-storage',
    }
  )
);
